import { z } from 'zod';

// ── Upload context types ─────────────────────────────────────────
export const uploadContextEnum = z.enum([
  'medida_foto',
  'planilla_foto',
  'extra_foto',
  'proforma_foto',
  'comprobante',
  'guia_transporte',
]);

export type UploadContext = z.infer<typeof uploadContextEnum>;

// ── Allowed MIME types ───────────────────────────────────────────
export const imageMimeTypes = ['image/jpeg', 'image/png', 'image/heic'] as const;
export const documentMimeTypes = ['application/pdf'] as const;
export const allMimeTypes = [...imageMimeTypes, ...documentMimeTypes] as const;

export const mimeTypeEnum = z.enum(allMimeTypes);

// ── Presign request schema ───────────────────────────────────────
export const presignUploadSchema = z.object({
  filename: z
    .string()
    .min(1, 'El nombre del archivo es requerido')
    .max(255, 'El nombre del archivo es demasiado largo'),
  mime_type: mimeTypeEnum,
  context: uploadContextEnum,
  entity_id: z.string().uuid('ID de entidad inválido').optional().nullable(),
});

export type PresignUploadInput = z.infer<typeof presignUploadSchema>;

// ── Presign response type ────────────────────────────────────────
export interface PresignedUploadResponse {
  upload_url: string;
  s3_key: string;
  expires_in: number;
}

// ── Download/view request ────────────────────────────────────────
export const downloadParamsSchema = z.object({
  s3Key: z.string().min(1, 'La clave S3 es requerida'),
});

export const downloadQuerySchema = z.object({
  key: z.string().min(1, 'La clave S3 es requerida'),
});

export type DownloadParams = z.infer<typeof downloadParamsSchema>;

// ── File size limits (bytes) ─────────────────────────────────────
export const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10 MB
export const MAX_PDF_SIZE = 20 * 1024 * 1024; // 20 MB
export const COMPRESSION_THRESHOLD = 5 * 1024 * 1024; // 5 MB

export function getMaxFileSize(mimeType: string): number {
  return mimeType === 'application/pdf' ? MAX_PDF_SIZE : MAX_IMAGE_SIZE;
}

// ── Context → allowed MIME types mapping ─────────────────────────
export function getAllowedMimeTypes(context: UploadContext): readonly string[] {
  switch (context) {
    case 'comprobante':
    case 'guia_transporte':
      return allMimeTypes;
    case 'medida_foto':
      return allMimeTypes;
    case 'planilla_foto':
    case 'extra_foto':
    case 'proforma_foto':
      return imageMimeTypes;
  }
}
